import { z } from 'zod';

import { BusinessOrganisationFeSchema } from './businessOrganisationSchema.ts';
import {
  createPaginatedResponseSchema,
  PagedRequestSchema,
} from './pagingSchema.ts';

export const UserFeSchema = z.object({
  id: z.uuid(),
  authProviderId: z.string().nullish(),
  userName: z.string(),
  employeeNumber: z.string().optional(),
  userEmail: z.string().optional(),
  firstName: z.string(),
  lastName: z.string(),
  status: z.string().nullish(),
  qualityRatingId: z.uuid().nullish(),
  qualityRatingName: z.string().optional(),
  skills: z.object({ code: z.string() }).array(),
  roles: z.array(z.uuid()),
  businessOrganisationId: z.uuid().nullable(),
  businessOrganisation: BusinessOrganisationFeSchema.nullish(),
  fullName: z.string().optional(),
  isExcluded: z.boolean().nullish().optional(),
  permissions: z
    .array(
      z.object({
        Name: z.string(),
        Access: z.boolean(),
      })
    )
    .nullish(),
  metadata: z
    .object({
      ava_resource: z.string().optional(),
      ava_resource_type: z.string().optional(),
    })
    .optional(),
});

export const UserSearchRequestSchema = z.object({
  'Include.AuditData': z.boolean().optional(),
  'Include.SchedulesData': z.boolean().optional(),
});

export const UserSearchResponseFESchema =
  createPaginatedResponseSchema(UserFeSchema);

// Models
export type UserFeModel = z.infer<typeof UserFeSchema>;
export type UserSearchRequestModel = z.infer<typeof UserSearchRequestSchema>;
export type UserSearchResponseFeModel = z.infer<
  typeof UserSearchResponseFESchema
>;

export const UserAssignedServiceSchema = z.object({
  id: z.string(),
  name: z.string().nullable(),
});

export const UserShiftSchema = z.object({
  id: z.string(),
  start: z.string(),
  end: z.string(),
  type: z.string(),
  reason: z.string(),
});
export const UserShiftFeRequestSchema = z.object({
  id: z.string(),
  shiftStartDate: z.string().optional(),
  pageSize: z.int().min(1).max(100).optional(),
});

export const UserShiftResponseBeSchema = z
  .object({
    results: z.array(UserShiftSchema),
  })
  .extend(PagedRequestSchema.shape);

export const UserSearchResultFESchema = z.object({
  id: z.uuid(),
  userName: z.string(),
  firstName: z.string(),
  lastName: z.string(),
  status: z.string().nullish(),
  qualityRatingId: z.uuid().nullish(),
  qualityRatingName: z.string().optional(),
  skills: z.object({ code: z.string() }).array(),
  roles: z.array(z.uuid()),
  businessOrganisationId: z.uuid().nullable(),
  businessOrganisation: BusinessOrganisationFeSchema.nullish(),
  metadata: z
    .object({
      ava_resource_type: z.string().optional(),
      ava_resource: z.string().optional(),
    })
    .optional(),
});

export const UserCreateUpdateSchema = z.object({
  id: z.uuid(),
  employeeNumber: z.string().min(1),
  userName: z.string().regex(/^\S*$/),
  firstName: z.string().regex(/^[A-Za-z'-]+$/),
  lastName: z.string().regex(/^[A-Za-z'-]+$/),
  qualityRatingId: z.uuid().nullish(),
  businessOrganisationId: z.string(),
  status: z.string(),
  skills: z.object({ code: z.string() }).array(),
  roles: z.uuid().array().min(1),
  metadata: z.object({
    ava_resource: z.string().optional(),
    ava_resource_type: z.string().optional(),
  }),
  isExcluded: z.boolean().nullish().optional(),
});

export const UserListSchema = z.array(UserFeSchema);

export const UserCreateDetailsSchema = z.object({
  id: z.uuid().optional(),
  employeeNumber: z.string().min(1),
  userName: z.string().regex(/^\S*$/),
  firstName: z.string().regex(/^[A-Za-z'-]+$/),
  lastName: z.string().regex(/^[A-Za-z'-]+$/),
  initialPassword: z.string().optional(),
  isExcluded: z.boolean().nullish().optional(),
});

export const ChangeUserPasswordSchema = z
  .object({
    password: z.string(),
    confirmPassword: z.string(),
  })
  .refine((schema) => schema.password === schema.confirmPassword, {
    error: 'PasswordsDontMatch',
    path: ['confirmPassword'],
  });

export const UserCreateOrganisationSchema = z.object({
  businessOrganisationId: z.string(),
  status: z.string(),
  qualityRatingId: z.uuid().optional(),
  metadata: z
    .object({
      ava_resource: z.string().optional(),
      ava_resource_type: z.string().optional(),
    })
    .optional(),
});

export const UserSkillSchema = z.object({ code: z.string() });

export const UserSkillListSchema = z.array(UserSkillSchema);

export const UserSkillsSchema = z.object({
  skills: UserSkillListSchema,
});

export const UserCreateSkillsSchema = z.object({
  data: UserSkillsSchema,
});

export const UserCreateRolesSchema = z.object({
  roles: z.uuid().array().min(1),
});

const UserRolesTableInfo = z.object({
  id: z.uuid(),
  name: z.string(),
  groups: z.array(z.string().nullish()).nullish(),
});

export const UserRolesAndPermissionsTableSchema = z.object({
  role: UserRolesTableInfo,
  skills: UserSkillListSchema,
  status: z.boolean(),
});

const UserFilterSchema = z.object({
  text: z.coerce.string<string>().optional(),
  id: z.uuid().optional(),
  name: z.coerce.string<string>().optional(),
  username: z.string().optional(),
  authProviderId: z.string().optional(),
  userEmail: z.string().optional(),
  firstName: z.string().optional(),
  lastName: z.string().optional(),
  roleIds: z.array(z.string()).or(z.string()).optional(),
});

export const UserSearchRequestFeSchema = UserFilterSchema.extend(
  PagedRequestSchema.shape
);

export const UserSearchFormFeSchema = UserFilterSchema.extend(
  PagedRequestSchema.shape
);
export const UserBusinessOrganisationSchemaUpdate = z.object({
  id: z.uuid(),
  businessOrganisationId: z.string(),
});
export const UserStatusSchemaUpdate = z.object({
  id: z.uuid(),
  status: z.string(),
});
export const UserQualityRatingSchemaUpdate = z.object({
  id: z.uuid(),
  qualityRatingId: z.uuid().nullish(),
});

export const UserErrorTypeEnum = z.enum([
  'required',
  'passwordsDontMatch',
  'invalidPassword',
]);

export type UserModel = z.infer<typeof UserFeSchema>;
export type UserSearchRequestFeModel = z.infer<
  typeof UserSearchRequestFeSchema
>;
export type UserSearchFeForm = z.infer<typeof UserSearchFormFeSchema>;
export type UserSearchResultFEModel = z.infer<typeof UserSearchResultFESchema>;
export type UserCreateDetailsModel = z.infer<typeof UserCreateDetailsSchema>;
export type ChangeUserPasswordModel = z.infer<typeof ChangeUserPasswordSchema>;
export type UserCreateOrganisationModel = z.infer<
  typeof UserCreateOrganisationSchema
>;
export type UserShiftFeRequestSchemaModel = z.infer<
  typeof UserShiftFeRequestSchema
>;
export type UserCreateSkillsModel = z.infer<typeof UserCreateSkillsSchema>;

export type UserSkillsModel = z.infer<typeof UserSkillsSchema>;
export type UserShiftModel = z.infer<typeof UserShiftSchema>;
export type UserSkillListModel = z.infer<typeof UserSkillListSchema>;
export type UserCreateRolesModel = z.infer<typeof UserCreateRolesSchema>;
export type UserShiftResponseBeModel = z.infer<
  typeof UserShiftResponseBeSchema
>;
export type UserBusinessOrganisationUpdateModel = z.infer<
  typeof UserBusinessOrganisationSchemaUpdate
>;
export type UserStatusUpdateModel = z.infer<typeof UserStatusSchemaUpdate>;
export type UserQualityRatingUpdateModel = z.infer<
  typeof UserQualityRatingSchemaUpdate
>;
export type UserCreateUpdateModel = z.infer<typeof UserCreateUpdateSchema>;

export const UserPasswordUpdateSchema = z.object({
  userId: z.uuid(),
  password: z.string(),
});
export type UserPasswordUpdateModel = z.infer<typeof UserPasswordUpdateSchema>;

export const UserAvaResourcesUpdateSchema = z.object({
  userId: z.uuid(),
  avaResourceType: z.string(),
  avaResource: z.string(),
});
export type UserAvaResourceUpdateModel = z.infer<
  typeof UserAvaResourcesUpdateSchema
>;
export type UserRolesAndPermissionsTableModel = z.infer<
  typeof UserRolesAndPermissionsTableSchema
>;

export const UserAvaResourcesGetSchema = z.object({
  avaResourceType: z.string(),
  avaResource: z.string(),
});
export type UserAvaResourceGetModel = z.infer<typeof UserAvaResourcesGetSchema>;

export const LinkedUserSchema = z.object({
  id: z.uuid(),
  username: z.string().nullish(),
  firstName: z.string().nullish(),
  lastName: z.string().nullish(),
  isScheduled: z.boolean().nullish(),
});

export type LinkedUserModel = z.infer<typeof LinkedUserSchema>;

const UserScheduleInfoSchema = z.object({
  start: z.string(),
  end: z.string(),
  type: z.enum(['Shift', 'Meal', 'Break']),
});

export const LinkedUserScheduleSchema = LinkedUserSchema.extend({
  schedules: UserScheduleInfoSchema.array().nullish(),
});

export type LinkedUserScheduleModel = z.infer<typeof LinkedUserScheduleSchema>;

export const UserInfoSchema = z.object({
  id: z.uuid(),
  employeeNumber: z.string().nullish(),
  authProviderId: z.string().nullish(),
  userName: z.string().nullish(),
  userEmail: z.string().nullish(),
  firstName: z.string().nullish(),
  lastName: z.string().nullish(),
  status: z.string().nullish(),
  qualityRatingId: z.uuid().nullish(),
  primaryServiceLocationId: z.uuid().nullish(),
  skills: z
    .array(
      z.object({
        code: z.string().nullish(),
        expiry: z.string().nullish(),
      })
    )
    .nullish(),
  roles: z.array(z.uuid()).nullish(),
  schedules: z.array(UserScheduleInfoSchema).nullish(),
  metadata: z
    .object({
      ava_resource: z.string().optional(),
      ava_resource_type: z.string().optional(),
    })
    .nullish(),
  businessOrganisationId: z.uuid().nullish(),
});

export type UserInfoModel = z.infer<typeof UserInfoSchema>;

export const SearchUsersResponseSchema =
  createPaginatedResponseSchema(UserInfoSchema);

export type SearchUsersResponseModel = z.infer<
  typeof SearchUsersResponseSchema
>;

export const BatchLinkedUserRequestSchema = z.object({
  locationId: z.uuid(),
  userIds: z.uuid().array(),
});
export type BatchLinkedUserRequestType = z.infer<
  typeof BatchLinkedUserRequestSchema
>;
